import React, { useState, useContext, useEffect, useMemo } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { IcChartCol, FaIcon } from "../../Icons";
import axios from "axios";
import Header from "../../layout/Header/Header";
import { Input, Label, Spinner } from "reactstrap";
import moment from "moment";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, LabelList, Cell,
} from "recharts";

// ── Helpers ───────────────────────────────────────────────────────────────────
const COLOR = "#6366f1";
const fmt   = (v) => "$" + new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(v || 0);
const fmtK  = (v) => {
  const n = Number(v) || 0;
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000)     return `$${(n / 1_000).toFixed(0)}k`;
  return fmt(n);
};

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: "#0f172a", borderRadius: 10, padding: "10px 16px",
      boxShadow: "0 8px 24px rgba(0,0,0,0.25)", fontSize: "0.78rem",
    }}>
      <div style={{ color: "#94a3b8", fontWeight: 700, marginBottom: 4, fontSize: "0.72rem" }}>{label}</div>
      <div style={{ color: "#fff", fontWeight: 800, fontSize: "0.92rem" }}>{fmt(payload[0]?.value)}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function GraficaVentasAnual() {
  const hoy = new Date();

  const { user }     = useContext(AuthContext);
  const URL_GRAFICAS = process.env.REACT_APP_URL_GRAFICAS;

  const [graficas,            setGraficas]            = useState([]);
  const [selectedFechaInicio, setSelectedFechaInicio] = useState("2021-01-01");
  const [selectedFechaFin,    setSelectedFechaFin]    = useState(moment(hoy).format("YYYY-MM-DD"));
  const [loading,             setLoading]             = useState(true);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${URL_GRAFICAS}/ventaAnual/${selectedFechaInicio}/${selectedFechaFin}`, {
        headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` },
      })
      .then((r) => { setGraficas(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [selectedFechaInicio, selectedFechaFin]); // eslint-disable-line

  const stats = useMemo(() => {
    if (!graficas.length) return null;
    const importes  = graficas.map((g) => Number(g.Importe) || 0);
    const total     = importes.reduce((s, v) => s + v, 0);
    const max       = Math.max(...importes);
    const maxIdx    = importes.indexOf(max);

    // Comparar promedio mensual para ser justo con años parciales
    const anioActualLocal = new Date().getFullYear();
    const mesActualLocal  = new Date().getMonth() + 1;
    const lastEntry  = graficas[graficas.length - 1];
    const prevEntry  = graficas[graficas.length - 2];
    const lastMeses  = lastEntry?.Año == String(anioActualLocal) ? mesActualLocal : 12;
    const prevMeses  = prevEntry?.Año == String(anioActualLocal) ? mesActualLocal : 12;
    const lastAvg    = (importes[importes.length - 1] || 0) / lastMeses;
    const prevAvg    = prevEntry ? (importes[importes.length - 2] || 0) / prevMeses : 0;
    const crecPct    = prevAvg > 0 ? ((lastAvg - prevAvg) / prevAvg * 100) : 0;

    return {
      total, max, count: importes.length,
      mejorAnio:  graficas[maxIdx]?.Año || "",
      ultimoAnio: lastEntry?.Año || "",
      ultimoVal:  importes[importes.length - 1] || 0,
      crecPct:    crecPct.toFixed(1),
      crecPos:    crecPct >= 0,
      lastMeses,
    };
  }, [graficas]); // eslint-disable-line

  // Destacar el año máximo con color más intenso
  const maxImporte = graficas.length ? Math.max(...graficas.map((g) => Number(g.Importe) || 0)) : 0;

  if (!user.usuarios_permisos) return null;

  return (
    <>
      <Header />
      <div className="card container col-sm-11" style={{ paddingBottom: 32 }}>

        {/* ── Page header ──────────────────────────────────────────────── */}
        <div className="page-header">
          <div>
            <h3 className="page-title">
              <IcChartCol style={{ marginRight: 8, color: COLOR }} />
              Venta Anual
            </h3>
            <p className="page-subtitle">Comparativo histórico de ingresos por año</p>
          </div>
          <div className="page-actions">
            <a href="/MenuReportes" style={{ fontSize: "0.78rem", color: "#64748b", textDecoration: "none" }}>
              ← Reportes
            </a>
          </div>
        </div>

        {/* ── Filter bar ───────────────────────────────────────────────── */}
        <div className="filter-bar">
          <div>
            <Label style={{ fontSize: "0.72rem", fontWeight: 600, color: "#64748b", marginBottom: 3, display: "block" }}>INICIO</Label>
            <Input bsSize="sm" type="date" value={selectedFechaInicio}
              onChange={(e) => setSelectedFechaInicio(e.target.value)} style={{ minWidth: 140 }} />
          </div>
          <div>
            <Label style={{ fontSize: "0.72rem", fontWeight: 600, color: "#64748b", marginBottom: 3, display: "block" }}>FIN</Label>
            <Input bsSize="sm" type="date" value={selectedFechaFin}
              onChange={(e) => setSelectedFechaFin(e.target.value)} style={{ minWidth: 140 }} />
          </div>
          {loading && <Spinner size="sm" style={{ color: COLOR, alignSelf: "flex-end", marginBottom: 4 }} />}
        </div>

        {/* ── KPI cards ────────────────────────────────────────────────── */}
        {stats && (
          <div className="row mb-3">
            {[
              { label: "Total histórico",  value: fmtK(stats.total),    sub: `${stats.count} años`, icon: "fas fa-chart-line",   color: COLOR,     bg: "#eef2ff" },
              { label: "Mejor año",        value: stats.mejorAnio,       sub: fmt(stats.max),        icon: "fas fa-trophy",       color: "#f59e0b", bg: "#fffbeb" },
              { label: `Año ${stats.ultimoAnio}`, value: fmtK(stats.ultimoVal), sub: "más reciente", icon: "fas fa-calendar-check", color: "#10b981", bg: "#f0fdf4" },
              {
                label: "Crecimiento",
                value: `${stats.crecPos ? "+" : ""}${stats.crecPct}%`,
                sub: `prom/mes · ${stats.lastMeses} meses`,
                icon: stats.crecPos ? "fas fa-arrow-trend-up" : "fas fa-arrow-trend-down",
                color: stats.crecPos ? "#10b981" : "#ef4444",
                bg:    stats.crecPos ? "#f0fdf4" : "#fff1f2",
              },
            ].map((card, i) => (
              <div key={i} className="col-6 col-md-3 mb-2">
                <div style={{ background: "#fff", borderRadius: 14, padding: "16px 18px",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.07)", borderTop: `3px solid ${card.color}`,
                  display: "flex", alignItems: "center", gap: 14, height: "100%" }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: card.bg,
                    flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <FaIcon cls={card.icon} size={18} style={{ color: card.color }} />
                  </div>
                  <div>
                    <div style={{ fontSize: "1.2rem", fontWeight: 800, color: "#0f172a", lineHeight: 1 }}>{card.value}</div>
                    <div style={{ fontSize: "0.67rem", fontWeight: 700, color: "#64748b",
                      textTransform: "uppercase", letterSpacing: "0.06em", marginTop: 4 }}>{card.label}</div>
                    <div style={{ fontSize: "0.72rem", color: card.color, fontWeight: 600, marginTop: 2 }}>{card.sub}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Chart ────────────────────────────────────────────────────── */}
        {loading ? (
          <div style={{ textAlign: "center", padding: "80px 0", color: "#94a3b8" }}>
            <Spinner style={{ color: COLOR, width: "2.5rem", height: "2.5rem" }} />
            <div style={{ marginTop: 14, fontWeight: 600 }}>Cargando datos…</div>
          </div>
        ) : graficas.length > 0 ? (
          <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0",
            padding: "20px 12px 12px", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
            <ResponsiveContainer width="100%" height={380}>
              <BarChart data={graficas} margin={{ top: 28, right: 16, left: 8, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradVentaAnual" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor={COLOR} stopOpacity={0.9} />
                    <stop offset="95%" stopColor={COLOR} stopOpacity={0.55} />
                  </linearGradient>
                  <linearGradient id="gradVentaAnualMax" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#4f46e5" stopOpacity={1} />
                    <stop offset="95%" stopColor="#4f46e5" stopOpacity={0.75} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="Año" tick={{ fontSize: 12, fill: "#94a3b8", fontWeight: 600 }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={fmtK} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={60} />
                <Tooltip content={<ChartTooltip />} cursor={{ fill: `${COLOR}10` }} />
                <Bar dataKey="Importe" radius={[7, 7, 0, 0]} maxBarSize={64}>
                  {graficas.map((entry, i) => (
                    <Cell
                      key={i}
                      fill={Number(entry.Importe) === maxImporte ? "url(#gradVentaAnualMax)" : "url(#gradVentaAnual)"}
                    />
                  ))}
                  <LabelList dataKey="Importe" position="top"
                    style={{ fontSize: "0.68rem", fontWeight: 700, fill: COLOR }}
                    formatter={(v) => fmtK(v)} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>
            <IcChartCol style={{ fontSize: "3rem", opacity: 0.2 }} />
            <div style={{ marginTop: 12, fontWeight: 600 }}>Sin datos en el período</div>
          </div>
        )}

      </div>
    </>
  );
}

export default GraficaVentasAnual;
